import { useState, useRef } from 'preact/hooks';
import { Group, Text, Box, Button, Image, ActionIcon, Paper, Stack, Alert } from '@mantine/core';
import { IconX, IconAlertCircle, IconFile, IconFileText } from '@tabler/icons-react';
import { ImageFile } from '@/types/chrome-ai';
import { isValidImageFile, createImageFile } from '@/lib/chrome-ai';

interface ImageUploadProps {
  images: ImageFile[];
  onImagesChange: (images: ImageFile[]) => void;
  maxImages?: number;
  disabled?: boolean;
}

export function ImageUpload({ 
  images, 
  onImagesChange, 
  maxImages = 5, 
  disabled = false 
}: ImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer?.files || []);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input.files) {
      const selectedFiles = Array.from(input.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = (files: File[]) => {
    setError('');
    
    const validImages: ImageFile[] = [];
    const errors: string[] = [];

    for (const file of files) {
      if (images.length + validImages.length >= maxImages) {
        errors.push(`Maximum ${maxImages} files allowed`);
        break;
      }

      if (!isValidImageFile(file)) {
        if (file.size > 50 * 1024 * 1024) {
          errors.push(`${file.name} is too large (max 50MB)`);
        } else {
          errors.push(`${file.name} is not a supported file type`);
        }
        continue;
      }

      validImages.push(createImageFile(file));
    }

    if (validImages.length > 0) {
      onImagesChange([...images, ...validImages]);
    }

    if (errors.length > 0) {
      setError(errors[0]);
    }
  };

  const removeImage = (imageId: string) => {
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.preview);
      onImagesChange(images.filter(img => img.id !== imageId));
    }
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  return (
    <Stack gap="sm">
      {/* Upload Area */}
      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragActive ? '#228be6' : disabled ? '#e9ecef' : '#dee2e6'}`,
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center',
          backgroundColor: dragActive ? '#f0f8ff' : disabled ? '#f8f9fa' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={!disabled ? openFileDialog : undefined}
      >
        <Stack align="center" gap="xs">
          <IconFile size={24} color={disabled ? '#adb5bd' : '#228be6'} />
          <div>
            <Text size="sm" fw={500} c={disabled ? 'dimmed' : undefined}>
              {dragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Text>
            <Text size="xs" c="dimmed">
              or click to select files
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              Images, PDFs, documents, code files (max 50MB each)
            </Text>
          </div>
        </Stack>
      </Box>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.txt,.md,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.js,.ts,.html,.css,.json,.py,.java,.c,.cpp"
        onChange={handleFileInput}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Error Message */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          onClose={() => setError('')}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="xs" fw={500}>
              Selected Files ({images.length}/{maxImages})
            </Text>
            <Button
              variant="outline"
              size="xs"
              color="red"
              onClick={() => {
                images.forEach(img => URL.revokeObjectURL(img.preview));
                onImagesChange([]);
              }}
            >
              Clear All
            </Button>
          </Group>

          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: '0.5rem',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {images.map((image) => (
              <Paper
                key={image.id}
                withBorder
                p="xs"
                radius="md"
                style={{ position: 'relative' }}
              >
                <ActionIcon
                  variant="filled"
                  color="red"
                  size="sm"
                  radius="xl"
                  onClick={() => removeImage(image.id)}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    zIndex: 1,
                  }}
                >
                  <IconX size={12} />
                </ActionIcon>

                {image.file.type.startsWith('image/') ? (
                  <Image
                    src={image.preview}
                    alt={image.name}
                    height={60}
                    fit="cover"
                    radius="sm"
                  />
                ) : (
                  <Box
                    style={{
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                    }}
                  >
                    <Stack align="center" gap={2}>
                      <IconFileText size={16} color="#228be6" />
                      <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: '60px', lineHeight: 1.2 }}>
                        {image.file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </Text>
                    </Stack>
                  </Box>
                )}

                <Text
                  size="xs"
                  c="dimmed"
                  mt="xs"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={image.name}
                >
                  {image.name}
                </Text>
                
                <Text size="xs" c="dimmed">
                  {(image.size / 1024 / 1024).toFixed(1)} MB
                </Text>
              </Paper>
            ))}
          </Box>
        </Stack>
      )}
    </Stack>
  );
}